/**
 * State mapping utilities for handling both state abbreviations and full state names
 * in a case-insensitive manner.
 */

/**
 * Creates lookup maps for efficient state normalization
 * @param {Object} stateAndCountiesMap - The mapping data containing state abbreviations and full names
 * @returns {Object} Object containing nameToAbbreviation and abbreviationToName maps
 */
export const createStateLookupMaps = (stateAndCountiesMap) => {
  const nameToAbbreviation = new Map()
  const abbreviationToName = new Map()

  if (!stateAndCountiesMap) {
    return { nameToAbbreviation, abbreviationToName }
  }

  Object.entries(stateAndCountiesMap).forEach(([stateCode, stateData]) => {
    const stateCodeUpper = stateCode.toUpperCase()
    const stateNameUpper = stateData.state_name?.toUpperCase()

    if (stateNameUpper) {
      // Map full state name to abbreviation (case-insensitive)
      nameToAbbreviation.set(stateNameUpper, stateCode)
      // Map abbreviation to full state name
      abbreviationToName.set(stateCodeUpper, stateData.state_name)
    }
  })

  return { nameToAbbreviation, abbreviationToName }
}

/**
 * Normalizes a state input to its standard abbreviation
 * Handles both abbreviations and full state names in a case-insensitive manner
 * @param {string} stateInput - The state input (abbreviation or full name)
 * @param {Object} stateAndCountiesMap - The mapping data containing state information
 * @returns {string|null} The normalized state abbreviation or null if not found
 */
export const normalizeStateInput = (stateInput, stateAndCountiesMap) => {
  if (!stateInput || !stateAndCountiesMap) {
    return null
  }

  const trimmedInput = stateInput.trim()
  if (!trimmedInput) {
    return null
  }

  const inputUpper = trimmedInput.toUpperCase()

  // First, check if it's already a valid abbreviation
  const directMatch = Object.keys(stateAndCountiesMap).find((stateCode) => stateCode.toUpperCase() === inputUpper)
  if (directMatch) {
    return directMatch
  }

  // Then, check if it's a full state name
  const matchingEntry = Object.entries(stateAndCountiesMap).find(
    ([, stateData]) => stateData.state_name?.toUpperCase() === inputUpper,
  )
  if (matchingEntry) {
    return matchingEntry[0]
  }

  return null
}

/**
 * Validates if a state input is valid (either abbreviation or full name)
 * @param {string} stateInput - The state input to validate
 * @param {Object} stateAndCountiesMap - The mapping data containing state information
 * @returns {boolean} True if the state is valid, false otherwise
 */
export const isValidState = (stateInput, stateAndCountiesMap) => {
  return normalizeStateInput(stateInput, stateAndCountiesMap) !== null
}

/**
 * Normalizes multiple state inputs to their standard abbreviations
 * @param {Array|string} statesInput - Array of states or comma-separated string
 * @param {Object} stateAndCountiesMap - The mapping data containing state information
 * @returns {Object} Object containing normalized states and invalid states
 */
export const normalizeStatesInput = (statesInput, stateAndCountiesMap) => {
  if (!statesInput) {
    return { normalizedStates: [], invalidStates: [] }
  }

  // Handle both array and comma-separated string inputs
  const statesArray = Array.isArray(statesInput)
    ? statesInput
    : statesInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

  const normalizedStates = []
  const invalidStates = []

  statesArray.forEach((stateInput) => {
    const normalized = normalizeStateInput(stateInput, stateAndCountiesMap)
    if (normalized) {
      normalizedStates.push(normalized)
    } else if (stateInput.trim()) {
      // Only add to invalid states if it's not an empty/whitespace-only string
      invalidStates.push(stateInput)
    }
  })

  return { normalizedStates, invalidStates }
}

/**
 * Normalizes county name for matching by removing special characters and converting to lowercase
 * @param {string} countyName - The county name to normalize
 * @returns {string} The normalized county name
 */
export const normalizeCountyName = (countyName) => {
  if (!countyName || typeof countyName !== 'string') {
    return ''
  }

  // Remove special characters except spaces and convert to lowercase
  return countyName
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '') // Remove all non-word characters except spaces
}

/**
 * Finds a matching county from the state data using case-insensitive and special character-insensitive matching
 * @param {string} inputCountyName - The county name input from user
 * @param {Object} stateCountiesData - The counties object from state data
 * @returns {string|null} The matching readable county name or null if not found
 */
export const findMatchingCounty = (inputCountyName, stateCountiesData) => {
  if (!inputCountyName || !stateCountiesData) {
    return null
  }

  const normalizedInput = normalizeCountyName(inputCountyName)

  // Find county by comparing normalized names (exact match only)
  const matchingEntry = Object.entries(stateCountiesData).find(([, countyData]) => {
    const normalizedReadable = normalizeCountyName(countyData.readable)
    return normalizedReadable === normalizedInput
  })

  return matchingEntry ? matchingEntry[1].readable : null
}

/**
 * Validates and normalizes county names for a given state
 * @param {Array|string} countiesInput - Array of counties or comma-separated string
 * @param {string} stateCode - The state code to validate counties against
 * @param {Object} stateAndCountiesMap - The mapping data containing state and county information
 * @returns {Object} Object containing normalized counties and invalid counties
 */
export const normalizeCountiesForState = (countiesInput, stateCode, stateAndCountiesMap) => {
  if (!countiesInput || !stateCode || !stateAndCountiesMap) {
    return { normalizedCounties: [], invalidCounties: [] }
  }

  const stateData = stateAndCountiesMap[stateCode]
  if (!stateData || !stateData.counties) {
    return { normalizedCounties: [], invalidCounties: [] }
  }

  // Handle both array and comma-separated string inputs
  const countiesArray = Array.isArray(countiesInput)
    ? countiesInput
    : countiesInput
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)

  const normalizedCounties = []
  const invalidCounties = []

  countiesArray.forEach((countyInput) => {
    const matchedCounty = findMatchingCounty(countyInput, stateData.counties)
    if (matchedCounty) {
      normalizedCounties.push(matchedCounty)
    } else if (countyInput.trim()) {
      // Only add to invalid counties if it's not an empty/whitespace-only string
      invalidCounties.push(countyInput)
    }
  })

  return { normalizedCounties, invalidCounties }
}

/**
 * Gets display options for state selection (both abbreviations and full names)
 * @param {Object} stateAndCountiesMap - The mapping data containing state information
 * @returns {Array} Array of option objects with value and label
 */
export const getStateOptions = (stateAndCountiesMap) => {
  if (!stateAndCountiesMap) {
    return []
  }

  return Object.entries(stateAndCountiesMap)
    .map(([stateCode, stateData]) => ({
      value: stateCode,
      label: `${stateCode} - ${stateData.state_name || stateCode}`,
      searchTerms: [stateCode, stateData.state_name].filter(Boolean),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
